import React, { useState } from 'react'
import AnnouncementIcon from '@material-ui/icons/AnnouncementOutlined'
import { Badge, IconButton, makeStyles, Popover, Tooltip } from '@paintscout/ui'
import { useResourceCenterEnabled } from 'shared/components/common/ResourceCenter/ResourceCenter'
import AnnouncementHistoryList from 'src/components/App/AnnouncementCenter/AnnouncementHistoryList'
import type { Announcement } from 'src/components/App/AnnouncementCenter/announcementQueue'
import { useAnnouncements } from 'src/components/App/AnnouncementCenter/useAnnouncements'
import { useHistoryAnnouncement } from 'src/components/App/AnnouncementCenter/useHistoryAnnouncement'

const useStyles = makeStyles({
  button: {
    color: 'inherit'
  },
  paper: {
    width: 340,
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 420
  }
})

/**
 * The header's announcement history (#25346): newest first, date stamped, with an unread badge.
 * Opening one marks it viewed, which clears it from the badge. Renders nothing with
 * `announcementCenter` off, during BugBug runs, or with `resourceCenterNext` on, where the history
 * lives in the resource center panel instead (#25353).
 */
export default function AnnouncementHistoryButton() {
  const classes = useStyles()
  const { enabled, history: announcements, unread, mark } = useAnnouncements()
  const resourceCenterNext = useResourceCenterEnabled()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const { show, view } = useHistoryAnnouncement(mark)

  if (!enabled || resourceCenterNext) return null

  const openAnnouncement = (announcement: Announcement) => {
    setAnchorEl(null)
    show(announcement)
  }

  return (
    <>
      <Tooltip title="Announcements" disableWrapper>
        <IconButton
          className={classes.button}
          aria-label={unread ? `Announcements, ${unread} unread` : 'Announcements'}
          onClick={(event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
          data-testid="announcement-history-button"
        >
          <Badge badgeContent={unread} color="error" max={9}>
            <AnnouncementIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        classes={{ paper: classes.paper }}
      >
        <AnnouncementHistoryList announcements={announcements} onSelect={openAnnouncement} />
      </Popover>
      {view}
    </>
  )
}
